import { IServerPagination } from "app/model"
import { CAMPAIGN_LANGUAGES } from "campaign/model"
import { CampaignAPI, MediaAPI, SspMediaApprovalAPI } from "generated-api"
import { ITimeFilter } from "ui/model"

export const CREATIVE_IMAGE_DOM_ID: string = "creative-image"
export const MAX_MEDIA_FILE_UPLOAD_SIZE_IN_BYTES = 200 * 1024 * 1024 // 2Mb
export const MEDIA_ERROR_URL: string = "adstanding_media_approval_error_url"

export const FILTER_MEDIA_APPROVAL_HEADER_HEIGHT_IN_REM = 2.7
export const FILTER_MEDIA_APPROVAL_BODY_HEIGHT_IN_REM = 5
export const FILTER_MEDIA_APPROVAL_EXPANDED_HEIGHT_IN_REM =
  FILTER_MEDIA_APPROVAL_HEADER_HEIGHT_IN_REM + FILTER_MEDIA_APPROVAL_BODY_HEIGHT_IN_REM

export type IMediaType = Awaited<ReturnType<typeof MediaAPI.getApiMediaTypeList>>[0]
export type IMediaDTO = Awaited<ReturnType<typeof MediaAPI.getApiMediaList>>[0]
export type ICampaignMediaRawDTO = Awaited<ReturnType<typeof CampaignAPI.getApiCampaignMediaList>>[0]
export type IMediaVideo = Awaited<ReturnType<typeof MediaAPI.getApiMediaVideoList>>[0]
export interface ICampaignMedia extends ICampaignMediaRawDTO {
  approval_status_id: APPROVAL_STATUSES
}
export type ICampaignMediaDTO = Required<NonNullable<ICampaignMedia["media"]>> & {
  approval_status_id: APPROVAL_STATUSES
}
export type ICampaignMediaCreativeDTO = ICampaignMedia
type ISspMediaApprovalDTO = Awaited<ReturnType<typeof SspMediaApprovalAPI.getApiSspMediaApprovalList>>[0]
export interface ISspMediaApproval extends ISspMediaApprovalDTO {
  media: IMediaDTO
  approval_status_id: MEDIA_APPROVAL_STATUSES
}
export type ISspMediaApprovalsByMedia = Awaited<
  ReturnType<typeof SspMediaApprovalAPI.getApiSspMediaApprovalMediaList>
>[0]
export type ISspMediaApprovalShort = Awaited<
  ReturnType<typeof SspMediaApprovalAPI.getApiSspMediaApprovalMediaList>
>[0]["ssp_approval"][0]

export enum APPROVAL_STATUSES {
  ACTIVE = "active",
  INACTIVE = "inactive",
  PENDING = "pending",
  REFUSED = "refused"
}

export enum MEDIA_APPROVAL_STATUSES {
  APPROVED = "approved",
  PENDING = "pending",
  REFUSED = "refused"
}
export const getAllMediaApprovalStatuses = (): MEDIA_APPROVAL_STATUSES[] => [
  MEDIA_APPROVAL_STATUSES.APPROVED,
  MEDIA_APPROVAL_STATUSES.PENDING,
  MEDIA_APPROVAL_STATUSES.REFUSED
]

export type IMediaCreateParams = Parameters<typeof MediaAPI.postApiMedia>[0]
export type IMediaVideoCreatParams = Parameters<typeof MediaAPI.postApiMediaVideo>[0]
export type ICampaignMediaCreative = Required<
  NonNullable<
    NonNullable<Awaited<ReturnType<typeof CampaignAPI.getApiCampaignMediaList>>[0]["media"]>["media_creative"]
  >[0]
>
export type ICampaignMediaVideo = Required<
  NonNullable<
    NonNullable<Awaited<ReturnType<typeof CampaignAPI.getApiCampaignMediaList>>[0]["media"]>["media_video"]
  >[0]
>

export enum MEDIA_TYPES {
  BANNER = "banner",
  BACKGROUND = "background",
  VIDEO = "video",
  CATFISH = "catfish",
  IN_IMAGE = "in_image",
  SPLASH = "splash",
  DIGITAL_BILLBOARD = "digital_billboard",
  AUDIO = "audio"
}

export enum MEDIA_SIMPLE_TYPES {
  VIDEO = "video",
  IMAGE = "image"
}

export enum MEDIA_CONTENT_TYPES {
  TEXT = "text",
  IMAGE = "banner",
  FLASH = "background",
  EXTERNAL = "external",
  SCRIPT = "script",
  VIDEO = "video",
  EXTERNAL_IMAGE = "external_image",
  EXTERNAL_VAST = "external_vast",
  HTML5 = "html5",
  AUDIO = "audio",
  EXTERNAL_DAAST = "external_daast",
  NATIVE = "native"
}

export type ICreativeFileType = "video" | "image" | "archive"

export interface IMediaApprovalData {
  media_id: number
  name: string
  type: MEDIA_SIMPLE_TYPES
  status: string
  mime_type: MEDIA_SIMPLE_TYPES
  filesize: number
  bitrate: number
  width: number
  height: number
  aspect_ratio: number
  advertiser_name: string
  category: string
  language: string
  created_at: string
  mediaApprovalsByMedia: ISspMediaApprovalsByMedia
}

export interface IMedia extends IMediaDTO {
  creatives: ICampaignMediaCreative[]
  videos: ICampaignMediaVideo[]
  campaignMediaId?: number
  approval_status_id?: APPROVAL_STATUSES
  refusal_note?: string | null
}

export interface IMediaTypeContent {
  id: number
  content_type: MEDIA_CONTENT_TYPES
  allowed_for_site: boolean
  allowed_for_network: boolean
  allowed_for_background: boolean
  available: boolean
  is_public: boolean
  is_formatted: boolean
}

export interface ISspMediaApprovalInfo {
  label: string
  value: string | number | JSX.Element
}

export interface IMediaRepository {
  getMediaTypes(): Promise<IMediaType[]>
  getMediaByName(name: string): Promise<IMediaDTO[]>
  getMediasById(ids: number[]): Promise<IMediaDTO[]>
  createMedia(params: IMediaCreateParams): Promise<IMedia>
  getCampaignMedias(campaignId: number): Promise<IMedia[]>
  getCampaignMedia(campaignId: number, mediaId: number): Promise<IMedia>
  createCampaignMedia(campaignId: number, mediaId: number): Promise<ICampaignMedia>
  deleteCampaignMedia(campaignId: number, mediaId: number): Promise<void>
  deleteMediaCreative(mediaId: number, creativeId: number): Promise<void>
  getMediaCreativeForecast(campaignId: number): Promise<IMediaCreativeForecastById>
  getMediaCreativeUploadUrl(mediaId: number): string
  getMediaCreativeDownloadUrl(mediaId: number, creativeId: number): string
  getMediaImageDownloadUrl(mediaId: number): string
  getMediaVideoDownloadUrl(mediaId: number, videoId: number): string
  createMediaVideo(params: IMediaVideoCreatParams): Promise<ICampaignMediaVideo>
  deleteMediaVideo(mediaId: number, videoId: number): Promise<void>
  getAllMediaApprovals(): Promise<IMediaApprovalData[]>
  getMediaApprovals(filters: ISspMediaApprovalFilters, pagination?: IServerPagination): Promise<ISspMediaApproval[]>
  getMediaApprovalsCount(filters?: ISspMediaApprovalFilters): Promise<number>
  getMediaVideos(mediaId: number): Promise<IMediaVideo[]>
  updateMediaApproval(mediaApproval: ISspMediaApproval, status: MEDIA_APPROVAL_STATUSES): Promise<ISspMediaApproval>
  getMediaPreviewUrl(mediaId: number): string
}

export interface IMediaStateService {
  progressTimeout: any
  creativeUploadRefreshTimeout: any
  mediaInfoById: { [mediaId: number]: IMediaInfo }
}

export interface IMediaUploadParams {
  campaignId: number
  isVideo?: boolean
  formatId?: number | null
  width?: number | null
  height?: number | null
}

export interface ICreateMediaParams extends IMediaUploadParams {
  mediaName: string
}

export interface ISspMediaApprovalFilters {
  name: string
  approvalStatuses: MEDIA_APPROVAL_STATUSES[]
  languages: CAMPAIGN_LANGUAGES[]
  createdAt: ITimeFilter
}

export interface ISspMediaApprovalState {
  mediaApprovalsData: IMediaApprovalData[]
  filteredMediaApprovalsData: IMediaApprovalData[]
  isLoading: boolean
  hasError: boolean
  filters: ISspMediaApprovalFilters
  isFilterExpanded: boolean
  isUpdating: boolean
}

export interface IMediaState {
  mediaTypes: IMediaType[]
  sspMediaApproval: ISspMediaApprovalState
}

export interface IMediaCreativeForecastInfo {
  forecastId: string
  mediaType: MEDIA_SIMPLE_TYPES
  width: number
  height: number
  formatId: number | null
}

export interface IMediaCreativeForecast extends IMediaCreativeForecastInfo {
  screen: number
  screenPercent: number
  screenTotal: number
  impression: number
  impressionPercent: number
  impressionTotal: number
  spendInCad: number
  spendTotalInCad: number
}

export interface IMediaCreativeForecastById {
  [generatedId: string]: IMediaCreativeForecast
}

export interface ICreativeUploadData {
  selectedFiles: FileList | null
  error: string
  isUploading: boolean
  progressPercent: number
}

export interface IMediaInfo {
  name: string
  campaignMediaId: number | null
}
